import React from 'react';
import { ToggleButtonGroup, Box, useMediaQuery, useTheme } from '@mui/material';
import { Check } from '@mui/icons-material';
import { StyledToggleButton } from './styled';

export interface ButtonGroupRadioSwitchOption {
  value: string;
  label: React.ReactNode;
  icon?: React.ReactNode;
}

interface BaseProps {
  options: ButtonGroupRadioSwitchOption[];
  includeAllOption?: boolean;
  allOptionLabel?: string;
  clapToVertical?: boolean;
  preventDeselection?: boolean;
}

interface SingleSelectProps extends BaseProps {
  multiple?: false;
  value: string;
  onChange: (value: string) => void;
}

interface MultiSelectProps extends BaseProps {
  multiple: true;
  value: string[];
  onChange: (value: string[]) => void;
}
// this hack allows TS to correctly change value and onChange types depending on multiple prop value
type ButtonGroupRadioSwitchProps = SingleSelectProps | MultiSelectProps;

export const ButtonGroupRadioSwitch = (props: ButtonGroupRadioSwitchProps) => {
  const {
    options,
    multiple,
    value,
    onChange,
    includeAllOption = false,
    allOptionLabel = 'Alle',
    // controls if buttons become vertical on mobile layout
    clapToVertical = true,
    preventDeselection = false,
  } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleChange = (_e: React.MouseEvent<HTMLElement>, val: string | string[]) => {
    // same as onChange(val) but TS can't infer right val type without it
    if (multiple) {
      const arrayVal = val as string[];
      // If "alle" is selected, set value to empty array
      if (includeAllOption && arrayVal.includes('alle')) {
        (onChange as (value: string[]) => void)([]);
      } else {
        // If preventDeselection is true, merge previous selections with new ones
        if (preventDeselection && Array.isArray(value)) {
          // Always keep existing selections and add any new ones
          const combinedSelections = [...new Set([...value, ...arrayVal])];
          (onChange as (value: string[]) => void)(combinedSelections);
        } else {
          (onChange as (value: string[]) => void)(arrayVal);
        }
      }
    } else {
      // For single select, prevent deselection if preventDeselection is true
      if (preventDeselection && !val && value) {
        // Don't change the value if trying to deselect the current selection
        return;
      }
      (onChange as (value: string) => void)(val as string);
    }
  };

  const isSelected = (optionValue: string) => {
    if (multiple) {
      // If "alle" option and value is empty array, it's selected
      if (includeAllOption && optionValue === 'alle') {
        return Array.isArray(value) && value.length === 0;
      }
      return Array.isArray(value) && value.includes(optionValue);
    } else {
      return value === optionValue;
    }
  };

  // Create all options array including "Alle" if needed
  const allOptions = React.useMemo(() => {
    if (includeAllOption && multiple) {
      return [{ value: 'alle', label: allOptionLabel }, ...options];
    }
    return options;
  }, [includeAllOption, multiple, allOptionLabel, options]);

  const shouldBeVertical = clapToVertical && isMobile;

  return (
    <Box display="flex" flexDirection={shouldBeVertical ? 'column' : 'row'} alignItems="flex-start">
      <ToggleButtonGroup
        exclusive={!multiple}
        value={value}
        onChange={handleChange}
        orientation={shouldBeVertical ? 'vertical' : 'horizontal'}
        sx={{
          boxShadow: 'none',
          width: 'auto',
        }}
      >
        {allOptions.map((option, idx) => (
          <StyledToggleButton
            key={option.value}
            value={option.value}
            selected={isSelected(option.value)}
            isFirst={idx === 0}
            isLast={idx === allOptions.length - 1}
            isSelected={isSelected(option.value)}
            isVertical={shouldBeVertical}
            sx={{
              width: 'auto',
            }}
          >
            {isSelected(option.value) && (
              <Box mr={1} display="flex" alignItems="center">
                {option.icon || <Check sx={{ fontSize: 14 }} />}
              </Box>
            )}
            {option.label}
          </StyledToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  );
};
